import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "./SiteHeader";
import SiteFooter from "./SiteFooter";
import { COLORS, SERIF, SANS, pageStyles } from "./dispute/theme";

// Shared layout for the long-form legal pages (Privacy Policy, Terms of Service).
// Uses the same page chrome as the rest of the app (SiteHeader + centered card).
// Content is passed as a `blocks` array so each page stays readable:
//   { h }   → section heading (e.g. "1. What Disputefai Is")
//   { sub } → sub-heading (e.g. "Account Information")
//   { p }   → paragraph
//   { ul }  → bulleted list (array of strings)
//   { ol }  → numbered list (array of strings)
export default function LegalPage({ title, effectiveDate, blocks = [] }) {
  // Long documents: start at the top when navigated to from a footer link.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [title]);

  return (
    <div style={pageStyles.root}>
      <SiteHeader />
      <div style={pageStyles.shell}>
        <div style={styles.card}>
          <div style={styles.titleBlock}>
            <span style={styles.eyebrow}>Legal</span>
            <h1 style={styles.title}>{title}</h1>
            {effectiveDate && <div style={styles.meta}>Effective Date: {effectiveDate}</div>}
            <Link to="/home" style={styles.backLink}>← Back to home</Link>
          </div>

          {blocks.map((b, i) => {
            if (b.h) return <h2 key={i} style={styles.h2}>{b.h}</h2>;
            if (b.sub) return <h3 key={i} style={styles.h3}>{b.sub}</h3>;
            if (b.p) return <p key={i} style={styles.p}>{b.p}</p>;
            if (b.ul)
              return (
                <ul key={i} style={styles.list}>
                  {b.ul.map((it, j) => <li key={j} style={styles.li}>{it}</li>)}
                </ul>
              );
            if (b.ol)
              return (
                <ol key={i} style={styles.list}>
                  {b.ol.map((it, j) => <li key={j} style={styles.li}>{it}</li>)}
                </ol>
              );
            return null;
          })}
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

const styles = {
  card: {
    width: "100%",
    maxWidth: 820,
    background: COLORS.card,
    borderRadius: 16,
    padding: "44px 48px",
    boxShadow: "0 6px 28px rgba(15, 30, 45, 0.06)",
    border: `1px solid ${COLORS.border}`,
  },
  titleBlock: { marginBottom: 28, borderBottom: `1px solid ${COLORS.border}`, paddingBottom: 24 },
  eyebrow: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: 2,
    color: COLORS.inkMuted,
    fontFamily: SANS,
  },
  title: {
    fontFamily: SERIF,
    fontSize: 34,
    fontWeight: 600,
    margin: "6px 0 10px 0",
    letterSpacing: "-0.5px",
    color: COLORS.ink,
    lineHeight: 1.15,
  },
  meta: { fontSize: 13, color: COLORS.inkMuted, fontFamily: SANS },
  backLink: {
    display: "inline-block",
    marginTop: 14,
    fontSize: 12,
    color: COLORS.inkSoft,
    textDecoration: "none",
    borderBottom: `1px dashed ${COLORS.borderStrong}`,
  },
  h2: {
    fontFamily: SERIF,
    fontSize: 21,
    fontWeight: 600,
    color: COLORS.ink,
    margin: "34px 0 10px 0",
    letterSpacing: "-0.2px",
  },
  h3: {
    fontFamily: SANS,
    fontSize: 14,
    fontWeight: 700,
    color: COLORS.ink,
    margin: "20px 0 6px 0",
  },
  p: {
    fontFamily: SANS,
    fontSize: 14.5,
    lineHeight: 1.7,
    color: COLORS.inkSoft,
    margin: "0 0 13px 0",
  },
  list: { margin: "0 0 14px 0", paddingLeft: 22 },
  li: {
    fontFamily: SANS,
    fontSize: 14.5,
    lineHeight: 1.6,
    color: COLORS.inkSoft,
    marginBottom: 6,
  },
};
